import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaEnvelope, FaMapMarkerAlt, FaComments } from 'react-icons/fa';
import PageSeo from '../components/PageSeo';
import Newsletters from './Newsletters';
import {
  CONTACT_CHANNELS,
  CONTACT_NEXT_STEPS,
  CONTACT_PROJECT_TYPES,
  CONTACT_PROOF_METRICS,
  CONTACT_SERVICE_ROUTES,
  HERO_RESPONSE_NODES,
} from '../data/contactPremium';
import { getEnv } from '../utils/env';
import '../styles/contact-premium.css';

const contactEndpoint = getEnv('CONTACT_MAIL_URL', '/api/mail/contact');

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CHANNEL_ICONS = {
  office: FaMapMarkerAlt,
  email: FaEnvelope,
  chat: FaComments,
};

const GetInTouch = () => {
  const [websiteurl, setWebsiteurl] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [details, setDetails] = useState('');
  const [projectTypes, setProjectTypes] = useState<string[]>([]);
  const [emailStatus, setEmailStatus] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggleProjectType = (label: string) => {
    setProjectTypes((current) =>
      current.includes(label)
        ? current.filter((item) => item !== label)
        : [...current, label],
    );
    setSubmitError('');
  };

  const submitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedDetails = details.trim();

    if (!trimmedName) {
      setSubmitError('Please enter your name.');
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setSubmitError('Please enter a valid email address.');
      return;
    }

    if (!trimmedDetails) {
      setSubmitError('Please tell us about your project or question.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setEmailStatus('');

    const typeLine =
      projectTypes.length > 0 ? `Project type(s): ${projectTypes.join(', ')}\n\n` : '';
    const fullDetails = `${typeLine}${trimmedDetails}`;

    const params = new URLSearchParams({
      sendto: trimmedEmail,
      websiteurl: websiteurl.trim(),
      name: trimmedName,
      phone: phone.trim(),
      details: fullDetails,
    });

    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      const ok = xhr.status >= 200 && xhr.status < 400;
      setSubmitting(false);
      if (ok) {
        setEmailStatus(
          xhr.responseText ||
            'Thank you — we have received your message and will get back to you soon.',
        );
        setWebsiteurl('');
        setName('');
        setEmail('');
        setPhone('');
        setDetails('');
        setProjectTypes([]);
      } else {
        setSubmitError(
          'We could not send your message. Please try again or email us directly.',
        );
      }
    });
    xhr.addEventListener('error', () => {
      setSubmitting(false);
      setSubmitError(
        'Network error. Please check your connection or email us directly.',
      );
    });
    xhr.open('GET', `${contactEndpoint}?${params.toString()}`);
    xhr.send();
  };

  return (
    <>
      <PageSeo path="/getintouch" />

      <div className="contact-premium">
        <div className="header-spacer" />

        <section className="contact-premium-hero" aria-labelledby="contact-premium-heading">
          <div className="contact-premium__shell">
            <div className="contact-premium-hero__layout">
              <div>
                <p className="contact-premium-hero__kicker">Project intake · Gautam Buddha Nagar, Uttar Pradesh</p>
                <h1 id="contact-premium-heading" className="contact-premium-hero__title">
                  Tell us what you want to build, fix, or scale
                </h1>
                <p className="contact-premium-hero__lead">
                  Share your stack, timeline, and goals — we will review your brief and respond
                  with next steps for web development, ecommerce, integrations, maintenance, or
                  AI automation.
                </p>
                <div className="contact-premium-hero__actions">
                  <a href="#contact-form" className="contact-premium-btn contact-premium-btn--primary">
                    Start your project brief
                  </a>
                  <a href="mailto:info@pluxinfosystems.com" className="contact-premium-btn contact-premium-btn--ghost">
                    Email us
                  </a>
                </div>
              </div>

              <div className="contact-premium-console" aria-hidden="true">
                {HERO_RESPONSE_NODES.map((node) => (
                  <div key={node} className="contact-premium-console__node">
                    {node}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="contact-premium-proof" aria-labelledby="contact-proof-heading">
          <div className="contact-premium__shell">
            <h2 id="contact-proof-heading" className="sr-only">
              Contact response proof
            </h2>
            <div className="contact-premium-proof__grid">
              {CONTACT_PROOF_METRICS.map((item) => (
                <article key={item.id} className="contact-premium-proof__card">
                  <span className="contact-premium-proof__value">{item.value}</span>
                  <span className="contact-premium-proof__label">{item.label}</span>
                  <p className="contact-premium-proof__detail">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-premium-section" aria-labelledby="contact-channels-heading">
          <div className="contact-premium__shell">
            <div className="contact-premium-section__header">
              <p className="contact-premium-section__kicker">Direct contact</p>
              <h2 id="contact-channels-heading" className="contact-premium-section__title">
                Reach our Gautam Buddha Nagar team by office, email, or phone
              </h2>
              <p className="contact-premium-section__intro">
                Prefer to talk first? Use the channels below — or scroll to the project brief form.
              </p>
            </div>

            <div className="contact-premium-channels">
              {CONTACT_CHANNELS.map((channel) => {
                const Icon = CHANNEL_ICONS[channel.id as keyof typeof CHANNEL_ICONS] || FaMapMarkerAlt;
                return (
                  <article key={channel.id} className="contact-premium-channel">
                    <span className="contact-premium-channel__icon" aria-hidden="true">
                      <Icon />
                    </span>
                    {channel.href ? (
                      <a href={channel.href} className="contact-premium-channel__title">
                        {channel.title}
                      </a>
                    ) : (
                      <h3 className="contact-premium-channel__title">{channel.title}</h3>
                    )}
                    <p className="contact-premium-channel__detail">{channel.detail}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section
          id="contact-form"
          className="contact-premium-form-section"
          aria-labelledby="contact-form-heading"
        >
          <div className="contact-premium__shell">
            <div className="contact-premium-section__header">
              <p className="contact-premium-section__kicker">Project brief</p>
              <h2 id="contact-form-heading" className="contact-premium-section__title">
                Start your project brief
              </h2>
              <p className="contact-premium-section__intro">
                Tell us what you need built, fixed, or integrated. Include stack, timeline, and
                anything specific you want us to review.
              </p>
            </div>

            <div className="contact-premium-form-layout">
              <form className="contact-premium-form" onSubmit={submitForm} noValidate>
                {submitError ? (
                  <p
                    id="contact-form-error"
                    className="contact-premium-form__alert contact-premium-form__alert--error"
                    role="alert"
                  >
                    {submitError}
                  </p>
                ) : null}
                {emailStatus ? (
                  <p
                    id="contact-form-status"
                    className="contact-premium-form__alert contact-premium-form__alert--success"
                    role="status"
                  >
                    {emailStatus}
                  </p>
                ) : null}

                <div className="contact-premium-form__types">
                  <span id="contact-project-types-label" className="contact-premium-form__types-label">
                    Project type (optional)
                  </span>
                  <span className="contact-premium-form__types-hint">
                    Select all that apply — we will include this in your brief.
                  </span>
                  <div
                    className="contact-premium-form__chips"
                    role="group"
                    aria-labelledby="contact-project-types-label"
                  >
                    {CONTACT_PROJECT_TYPES.map(({ id, label }) => {
                      const selected = projectTypes.includes(label);
                      return (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={selected}
                          className={`contact-premium-form__chip${selected ? ' is-active' : ''}`}
                          onClick={() => toggleProjectType(label)}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="contact-premium-form__grid">
                  <div className="contact-premium-form__field">
                    <label htmlFor="contact-name" className="contact-premium-form__label">
                      Your name
                    </label>
                    <input
                      id="contact-name"
                      className="contact-premium-form__input"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setSubmitError('');
                      }}
                      type="text"
                      autoComplete="name"
                      required
                    />
                  </div>
                  <div className="contact-premium-form__field">
                    <label htmlFor="contact-email" className="contact-premium-form__label">
                      Email address
                    </label>
                    <input
                      id="contact-email"
                      className="contact-premium-form__input"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setSubmitError('');
                      }}
                      type="email"
                      autoComplete="email"
                      required
                      aria-invalid={submitError ? 'true' : undefined}
                      aria-describedby={submitError ? 'contact-form-error' : undefined}
                    />
                  </div>
                  <div className="contact-premium-form__field">
                    <label htmlFor="contact-phone" className="contact-premium-form__label">
                      Phone
                    </label>
                    <input
                      id="contact-phone"
                      className="contact-premium-form__input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      type="tel"
                      autoComplete="tel"
                    />
                  </div>
                  <div className="contact-premium-form__field">
                    <label htmlFor="contact-website" className="contact-premium-form__label">
                      Website URL
                    </label>
                    <input
                      id="contact-website"
                      className="contact-premium-form__input"
                      value={websiteurl}
                      onChange={(e) => setWebsiteurl(e.target.value)}
                      type="url"
                      autoComplete="url"
                      placeholder="https://"
                    />
                  </div>
                  <div className="contact-premium-form__field contact-premium-form__field--full">
                    <label htmlFor="contact-details" className="contact-premium-form__label">
                      Project details
                    </label>
                    <textarea
                      id="contact-details"
                      className="contact-premium-form__textarea"
                      value={details}
                      onChange={(e) => {
                        setDetails(e.target.value);
                        setSubmitError('');
                      }}
                      placeholder="Describe your project, timeline, stack, and anything specific you want us to review."
                      required
                    />
                  </div>
                </div>

                <div className="contact-premium-form__footer">
                  <button
                    type="submit"
                    className="contact-premium-btn contact-premium-btn--primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending…' : 'Submit project brief'}
                  </button>
                  <p className="contact-premium-form__hint">
                    We reply by email or on-site chat — no phone calls.
                  </p>
                </div>
              </form>

              <aside className="contact-premium-form-aside" aria-label="Direct contact fallback">
                <div className="contact-premium-form-aside__card">
                  <h3 className="contact-premium-form-aside__title">Prefer direct contact?</h3>
                  <ul className="contact-premium-form-aside__list">
                    <li>
                      <button
                        type="button"
                        className="contact-premium-form-aside__link"
                        onClick={() => {
                          const launcher = document.querySelector('.pluxcb-launcher') as HTMLButtonElement | null;
                          launcher?.click();
                        }}
                      >
                        <FaComments aria-hidden="true" />
                        Open chat
                      </button>
                    </li>
                    <li>
                      <a
                        href="mailto:info@pluxinfosystems.com"
                        className="contact-premium-form-aside__link"
                      >
                        <FaEnvelope aria-hidden="true" />
                        info@pluxinfosystems.com
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="contact-premium-form-aside__card">
                  <h3 className="contact-premium-form-aside__title">Office</h3>
                  <p className="contact-premium-channel__detail">
                    135, Rajpuram, Chipiyana Buzurg, Gautam Buddha Nagar, Uttar Pradesh 201009
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="contact-premium-section" aria-labelledby="contact-steps-heading">
          <div className="contact-premium__shell">
            <div className="contact-premium-section__header">
              <p className="contact-premium-section__kicker">What happens next</p>
              <h2 id="contact-steps-heading" className="contact-premium-section__title">
                A clear path from brief to delivery kickoff
              </h2>
              <p className="contact-premium-section__intro">
                We keep the intake process straightforward so you know what to expect after you
                submit your project brief.
              </p>
            </div>

            <div className="contact-premium-steps">
              {CONTACT_NEXT_STEPS.map((step) => (
                <article key={step.id} className="contact-premium-step">
                  <span className="contact-premium-step__number">{step.step}</span>
                  <h3 className="contact-premium-step__title">{step.title}</h3>
                  <p className="contact-premium-step__detail">{step.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-premium-section" aria-labelledby="contact-routes-heading">
          <div className="contact-premium__shell">
            <div className="contact-premium-section__header">
              <p className="contact-premium-section__kicker">Service pathways</p>
              <h2 id="contact-routes-heading" className="contact-premium-section__title">
                Explore services related to your project
              </h2>
              <p className="contact-premium-section__intro">
                Not sure where to start? Browse service pages and delivery insights before you
                submit your brief.
              </p>
            </div>

            <div className="contact-premium-routes">
              {CONTACT_SERVICE_ROUTES.map((route) => (
                <article key={route.id} className="contact-premium-route">
                  <h3 className="contact-premium-route__title">{route.title}</h3>
                  <p className="contact-premium-route__summary">{route.summary}</p>
                  <Link
                    to={route.href}
                    className="contact-premium-btn contact-premium-btn--secondary contact-premium-route__cta"
                  >
                    {route.label}
                    <FaArrowRight aria-hidden="true" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <Newsletters />
      </div>
    </>
  );
};

export default GetInTouch;
